// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a globe's floating legend stays clear of the screen's own controls.
 *
 * Each globe's legend sits in its panel's bottom-right corner. With one
 * globe that corner is the screen's, and the stylesheet lifts it above the
 * Tools bar and the transport (`colorbar.css`). With two or four globes a
 * legend can land on the information card, the chatbot's button or the
 * transport (a phone's two globes side by side put the first globe's
 * legend on the information card). This lifts such a legend, by the least
 * that clears what it covers, through `--legend-lift`; a legend the
 * reader has moved (`legendPlacement.ts`) is left where it was put.
 */

/** A box in viewport pixels. */
export interface Box {
  readonly left: number
  readonly right: number
  readonly top: number
  readonly bottom: number
}

/** The controls that can occupy the bottom of the screen. */
const OBSTACLE_IDS = ['playback-controls', 'map-controls', 'info-panel', 'ayni-chat-trigger', 'geo-media-panel']

const LEGEND_SELECTOR = '.map-viewport .panel-colorbar, .map-viewport .panel-legend:not(.hidden)'

/**
 * How far to raise `legend` (px) so it covers none of `obstacles`, at
 * least `gap` above each it would cover; raised again past whatever the
 * first rise lands it on. Never more than `maxLift` (its panel's room):
 * past that it stays at `maxLift`, the best it can do.
 */
export function legendLift(legend: Box, obstacles: readonly Box[], gap: number, maxLift: number): number {
  let lift = 0
  for (let pass = 0; pass < obstacles.length + 1; pass++) {
    const top = legend.top - lift
    const bottom = legend.bottom - lift
    let need = lift
    for (const o of obstacles) {
      const across = Math.min(legend.right, o.right) - Math.max(legend.left, o.left)
      const down = Math.min(bottom, o.bottom) - Math.max(top, o.top)
      if (across > 0.5 && down > 0.5) need = Math.max(need, legend.bottom - o.top + gap)
    }
    if (need === lift) break
    lift = need
  }
  return Math.max(0, Math.min(lift, maxLift))
}

/** Lift every globe's legend clear of the controls (main.ts after drawing them, and on resize). */
export function clearPanelLegends(): void {
  if (typeof document === 'undefined') return
  const obstacles: Box[] = []
  for (const id of OBSTACLE_IDS) {
    const el = document.getElementById(id)
    if (!el || el.classList.contains('hidden')) continue
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) obstacles.push({ left: r.left, right: r.right, top: r.top, bottom: r.bottom })
  }
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  for (const el of document.querySelectorAll<HTMLElement>(LEGEND_SELECTOR)) {
    // Moved by the reader: their place.
    if (el.style.top) {
      el.style.removeProperty('--legend-lift')
      continue
    }
    el.style.removeProperty('--legend-lift')
    const r = el.getBoundingClientRect()
    if (r.width === 0) continue
    const panel = el.closest('.map-viewport')?.getBoundingClientRect()
    const room = panel ? Math.max(0, r.top - panel.top - 0.5 * rem) : 0
    const lift = legendLift({ left: r.left, right: r.right, top: r.top, bottom: r.bottom }, obstacles, 0.5 * rem, room)
    if (lift > 0) el.style.setProperty('--legend-lift', `${Math.round(lift)}px`)
  }
}
